import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Pencil, Plus, RefreshCw, Star, Trash2 } from 'lucide-react'
import {
  useTestimonials,
  useCreateTestimonial,
  useUpdateTestimonial,
  useDeleteTestimonial,
} from '@/hooks/data/useTestimonials'
import { useCatalog } from '@/hooks/data/useBookings'
import { imagesApi } from '@/api/images'
import type { Testimonial, TestimonialInput } from '@/types/domain'
import EditModal from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { InlineToggle } from '@/components/crud/InlineToggle'
import { resolveMediaUrl } from '@/utils/mediaUrl';

const RATING_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${'★'.repeat(n)}${'☆'.repeat(5 - n)} (${n})` }))

function Stars({ rating }: { rating: number }) {
  return (
    <span className="text-yellow-500 text-sm" title={`${rating}/5`}>
      {'★'.repeat(rating)}
      <span className="text-gray-300 dark:text-gray-600">{'★'.repeat(5 - rating)}</span>
    </span>
  )
}

function TestimonialsPage() {
  const { data: testimonials = [], isLoading, isError, error, isFetching, refetch } = useTestimonials()
  const { data: catalog } = useCatalog()
  const create = useCreateTestimonial()
  const update = useUpdateTestimonial()
  const del = useDeleteTestimonial()
  const confirmDelete = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Testimonial | null>(null)

  const rows = [...testimonials].sort((a, b) => a.sortOrder - b.sortOrder)

  const serviceOptions = [
    { value: '', label: 'None' },
    ...(catalog?.packages ?? []).map((p) => ({ value: p.name, label: p.name })),
  ]

  const fields = [
    { key: 'name', label: 'Customer Name', required: true },
    { key: 'location', label: 'Location' },
    { key: 'rating', label: 'Rating', type: 'select' as const, required: true, options: RATING_OPTIONS },
    { key: 'content', label: 'Testimonial (keep it under ~500 words)', type: 'textarea' as const, required: true },
    { key: 'serviceUsed', label: 'Service Used', type: 'select' as const, options: serviceOptions },
    {
      key: 'imageUrl',
      label: 'Avatar',
      type: 'file' as const,
      previewKind: 'image' as const,
      accept: 'image/jpeg,image/png,image/webp',
      uploadWithProgressFn: (file: File, onProgress: (pct: number) => void) =>
        imagesApi.uploadWithProgress(file, 'testimonials', onProgress).then((stored) => stored.url),
      maxSizeMB: 5,
      minDimensions: { width: 100, height: 100 },
      hint: 'JPG, PNG or WebP, up to 5MB, at least 100×100px.',
    },
    { key: 'givenOn', label: 'Date', placeholder: 'YYYY-MM-DD (defaults to today)' },
    {
      key: 'isFeatured',
      label: 'Featured',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'false', label: 'No' },
        { value: 'true', label: 'Yes' },
      ],
    },
    {
      key: 'published',
      label: 'Status',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'true', label: 'Published' },
        { value: 'false', label: 'Draft' },
      ],
    },
  ]

  const toTestimonialInput = (values: Record<string, any>, sortOrder: number): TestimonialInput => ({
    name: values.name,
    location: values.location ?? '',
    rating: Number(values.rating),
    content: values.content,
    imageUrl: values.imageUrl || undefined,
    sortOrder,
    givenOn: values.givenOn ? new Date(`${values.givenOn}T00:00:00.000Z`).toISOString() : undefined,
    published: values.published === 'true',
    serviceUsed: values.serviceUsed || undefined,
    isFeatured: values.isFeatured === 'true',
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Star className="w-6 h-6" /> Testimonials
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage customer testimonials (TestimonialsController).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Testimonial
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading testimonials…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load testimonials: {(error as Error)?.message}.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-gray-500">No testimonials found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2">Rating</th>
                <th className="px-4 py-2">Service Used</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Featured</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {t.imageUrl ? (
                        <img src={resolveMediaUrl(t.imageUrl)} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover bg-gray-100 dark:bg-gray-700" />
                      ) : (
                        <div className="h-8 w-8 shrink-0 rounded-full bg-gray-100 dark:bg-gray-700" />
                      )}
                      <div>
                        <div className="font-medium text-gray-800 dark:text-gray-100">{t.name}</div>
                        {t.location && <div className="text-xs text-gray-500">{t.location}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Stars rating={t.rating} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{t.serviceUsed || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{t.givenOn ? new Date(t.givenOn).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3">
                    {t.isFeatured && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        Featured
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <InlineToggle
                      checked={t.published}
                      label={t.published ? 'Published' : 'Draft'}
                      onToggle={(next) =>
                        update.mutateAsync({
                          id: t.id,
                          body: {
                            name: t.name,
                            location: t.location ?? '',
                            rating: t.rating,
                            content: t.content,
                            imageUrl: t.imageUrl || undefined,
                            sortOrder: t.sortOrder,
                            givenOn: t.givenOn,
                            published: next,
                            serviceUsed: t.serviceUsed || undefined,
                            isFeatured: t.isFeatured,
                          },
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(t)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit testimonial">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(t.name, async () => {
                            await del.mutateAsync(t.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete testimonial"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Testimonial"
        fields={fields}
        initialValues={{ rating: '5', isFeatured: 'false', published: 'true' }}
        submitLabel="Create"
        onSubmit={async (values) => {
          const nextOrder = rows.length ? Math.max(...rows.map((r) => r.sortOrder)) + 1 : 0
          await create.mutateAsync(toTestimonialInput(values, nextOrder))
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Testimonial"
        description={editing?.name}
        fields={fields}
        initialValues={
          editing
            ? {
                name: editing.name,
                location: editing.location,
                rating: String(editing.rating),
                content: editing.content,
                serviceUsed: editing.serviceUsed ?? '',
                imageUrl: editing.imageUrl,
                givenOn: editing.givenOn?.slice(0, 10),
                isFeatured: editing.isFeatured ? 'true' : 'false',
                published: editing.published ? 'true' : 'false',
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          await update.mutateAsync({ id: editing.id, body: toTestimonialInput(values, editing.sortOrder) })
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/testimonials/')({
  component: TestimonialsPage,
})
